import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Small motion toolkit. The rules every animation on the site follows:
 *
 * 1. The server renders the FINAL state. Nothing is hidden waiting for JS, so
 *    content is readable without scripts and never flashes.
 * 2. Motion is opt-in per element and only for things that start off-screen
 *    ("armed" before the visitor gets there), so nothing visible jumps back.
 * 3. `prefers-reduced-motion: reduce` turns every JS-driven animation off;
 *    the CSS side mirrors it with a media query.
 */

const useIsomorphicLayoutEffect =
	typeof document === 'undefined' ? useEffect : useLayoutEffect

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

export function prefersReducedMotion() {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia(REDUCED_MOTION).matches
	)
}

/** Live `prefers-reduced-motion` flag; `false` during SSR. */
export function usePrefersReducedMotion() {
	const [reduced, setReduced] = useState(false)
	useEffect(() => {
		if (typeof window.matchMedia !== 'function') return
		const query = window.matchMedia(REDUCED_MOTION)
		setReduced(query.matches)
		const onChange = () => setReduced(query.matches)
		query.addEventListener?.('change', onChange)
		return () => query.removeEventListener?.('change', onChange)
	}, [])
	return reduced
}

/**
 * A clock that starts at the server's "now" (so the server render and the
 * first client render agree) and then ticks on the client, aligned to whole
 * `intervalMs` boundaries.
 */
export function useNow(serverNow: number | Date, intervalMs = 1000) {
	const [now, setNow] = useState(() => new Date(serverNow))
	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>
		const tick = () => {
			setNow(new Date())
			timer = setTimeout(tick, intervalMs - (Date.now() % intervalMs))
		}
		tick()
		return () => clearTimeout(timer)
	}, [intervalMs])
	return now
}

export type RevealState =
	/** server render / not animated: final state */
	| 'static'
	/** off-screen at mount: showing the start state, waiting to be seen */
	| 'armed'
	/** scrolled into view: animating to the final state */
	| 'revealed'

/**
 * Reveal-once choreography for an element. Elements already on screen when
 * the page loads stay `static`; elements below the fold are `armed` before
 * the first paint and flip to `revealed` when they scroll into view.
 */
export function useRevealOnce<T extends Element>({
	threshold = 0.25,
	rootMargin = '0px 0px -10% 0px',
}: { threshold?: number; rootMargin?: string } = {}) {
	const ref = useRef<T>(null)
	const [state, setState] = useState<RevealState>('static')

	useIsomorphicLayoutEffect(() => {
		const el = ref.current
		if (!el || prefersReducedMotion()) return
		if (typeof IntersectionObserver === 'undefined') return
		const rect = el.getBoundingClientRect()
		const offscreen = rect.top > window.innerHeight || rect.bottom < 0
		if (!offscreen) return
		setState('armed')
		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry?.isIntersecting) return
				setState('revealed')
				observer.disconnect()
			},
			{ threshold, rootMargin },
		)
		observer.observe(el)
		return () => observer.disconnect()
	}, [threshold, rootMargin])

	return [ref, state] as const
}

/**
 * Whether an element is (roughly) on screen — used to pause ambient loops
 * that nobody can see. Defaults to `true` so SSR and old browsers animate.
 */
export function useInViewport<T extends Element>(rootMargin = '100px') {
	const ref = useRef<T>(null)
	const [inView, setInView] = useState(true)
	useEffect(() => {
		const el = ref.current
		if (!el || typeof IntersectionObserver === 'undefined') return
		const observer = new IntersectionObserver(
			([entry]) => setInView(Boolean(entry?.isIntersecting)),
			{ rootMargin },
		)
		observer.observe(el)
		return () => observer.disconnect()
	}, [rootMargin])
	return [ref, inView] as const
}

/** easeOutExpo: fast start, long gentle settle — the site's "arrive" curve */
export const easeOutExpo = (t: number) =>
	t >= 1 ? 1 : 1 - Math.pow(2, -10 * t)

/**
 * Deterministic pseudo-random numbers (mulberry32) so decorative scatter —
 * stars, notes — is identical on the server and the client.
 */
export function seededRandom(seed: number) {
	let a = seed >>> 0
	return () => {
		a = (a + 0x6d2b79f5) >>> 0
		let t = a
		t = Math.imul(t ^ (t >>> 15), t | 1)
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296
	}
}
